import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight } from "lucide-react";
import { scrollToId } from "./bits";

const Navbar = ({ t, lang, setLang }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const homeLabel = lang === "tr" ? "Ana Sayfa" : "Home";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const goHome = () => {
    setOpen(false);
    navigate("/");
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled ? "bg-[#E9EDF3]/85 backdrop-blur-xl border-[#CBD3DF]" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <button
          data-testid="nav-logo-link"
          onClick={() => go("top")}
          className="flex items-center gap-3 shrink-0"
        >
          <img src="/milsis-logo.png" alt="MILSIS" className="h-10 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          <button
            data-testid="nav-link-home"
            onClick={goHome}
            className="font-mono-tech text-xs uppercase tracking-[0.2em] text-neutral-700 hover:text-emerald transition-colors duration-300"
          >
            {homeLabel}
          </button>
          {t.links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="font-mono-tech text-xs uppercase tracking-[0.2em] text-neutral-700 hover:text-emerald transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center border border-[#CBD3DF] rounded-sm overflow-hidden" data-testid="nav-lang-toggle">
            {["tr", "en"].map((l) => (
              <button
                key={l}
                data-testid={`nav-lang-toggle-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 font-mono-tech text-xs uppercase tracking-widest transition-colors duration-300 ${
                  lang === l ? "bg-emerald text-[#E9EDF3] font-semibold" : "text-neutral-700 hover:text-[#0B0D10]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-cta-rfq-button"
            onClick={() => go("contact")}
            className="hidden sm:flex items-center gap-1.5 bg-emerald text-[#E9EDF3] font-display font-bold uppercase tracking-wider text-base px-5 py-2 rounded-sm hover:bg-emerald-dim transition-colors duration-300"
          >
            {t.cta}
            <ChevronRight size={16} strokeWidth={3} />
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-neutral-800 hover:text-emerald transition-colors p-1"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-[#E9EDF3]/95 backdrop-blur-xl border-b border-[#CBD3DF]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              <button
                data-testid="nav-mobile-link-home"
                onClick={goHome}
                className="text-left font-display font-bold uppercase tracking-wider text-xl text-neutral-900 hover:text-emerald transition-colors"
              >
                {homeLabel}
              </button>
              {t.links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-display font-bold uppercase tracking-wider text-xl text-neutral-900 hover:text-emerald transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta-rfq-button"
                onClick={() => go("contact")}
                className="mt-2 flex items-center justify-center gap-2 bg-emerald text-[#E9EDF3] font-display font-bold uppercase tracking-wider px-5 py-3 rounded-sm"
              >
                {t.cta}
                <ChevronRight size={16} strokeWidth={3} />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
